import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import SupportTeamApi from '@/extensions/tickets/store/api/supportTeam';

/**
 * The people working a desk, and what one of them booked.
 *
 * The span lives here rather than in each screen: the list and the detail are
 * read for the same period, and a person who narrows the list to last month
 * expects the page they open next to still be about last month.
 */
export const useSupportTeamStore = defineStore('supportTeam', {
    state: () => ({
        loading: true,
        span: null,
        teamPaginatedRequest: 0,
        detailRequest: 0,
        detail: null,
        teamPaginated: {
            variables: {
                order: 'logged_time',
                direction: 'desc',
                paginationSizes: [25, 50, 100],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: [],
        },
    }),

    actions: {
        defineSpan(span) {
            this.span = span;
        },

        addPaginatedEntriesToState(data) {
            this.teamPaginated.items = data.items;
            this.teamPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.teamPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.teamPaginated.link = _.cloneDeep(data.config.link);
            this.teamPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.teamPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            const activeColumns = [];
            _.each(this.teamPaginated.allColumns, (column) => {
                const allowed = _.find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.teamPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.teamPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.teamPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (
                value.match === '' &&
                value.like === '' &&
                value.from === '' &&
                value.to === '' &&
                value.in.length === 0
            ) {
                Vue.delete(this.teamPaginated.filters, key);
            } else {
                Vue.set(this.teamPaginated.filters, key, value);
            }
        },

        defineSort({ key, value }) {
            this.teamPaginated.variables.order = key;
            this.teamPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.teamPaginated.showFilters = value;
        },

        fetchTeamPaginated({ desk, cmsId }) {
            const request = ++this.teamPaginatedRequest;

            return SupportTeamApi.fetchPaginated(this.teamPaginated, {
                desk,
                cmsId,
                from: _.get(this.span, 'from'),
                to: _.get(this.span, 'to'),
            }).then((data) => {
                // A newer request went out while this one was in flight.
                if (request !== this.teamPaginatedRequest) return;

                this.addPaginatedEntriesToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchDetail({ userId, desk, cmsId }) {
            // Same guard the paginated fetches carry: the timespan chips fire
            // one of these per click, and a slower earlier span would otherwise
            // land its figures on top of the one now selected.
            const request = ++this.detailRequest;

            // This state outlives the page. Opening a second person otherwise
            // rendered their page on the first one's detail until the answer
            // landed - and the embedded gantt, created with that stale user,
            // stayed on the first person's tickets for good.
            if (String(_.get(this.detail, 'user.id')) !== String(userId)) {
                this.detail = null;
            }

            return SupportTeamApi.fetchDetail({
                userId,
                desk,
                cmsId,
                from: _.get(this.span, 'from'),
                to: _.get(this.span, 'to'),
            }).then((detail) => {
                if (request !== this.detailRequest) return this.detail;

                this.detail = detail;

                return detail;
            });
        },

        savePlatformTeamMember(payload) {
            return SupportTeamApi.savePlatformTeamMember(payload).then((saved) => {
                // Merge the answer into the detail the screen is already
                // showing, rather than asking for it again. The save returns
                // exactly the parts it changed; the metrics beside them are
                // derived from time entries over a span and cannot have moved.
                // MERGE the user rather than replace it. The save answers with
                // the same fields the fetch does, but a payload that ever
                // narrows again must not blank what it did not mention - that
                // is how the header lost the person's name.
                this.detail = {
                    ...(this.detail || {}),
                    user: { ..._.get(this.detail, 'user', {}), ...(saved.user || {}) },
                    administrations: saved.administrations,
                    websiteCount: saved.websiteCount,
                    vacationBalance: saved.vacationBalance || null,
                    intakeKey: saved.intakeKey || null,
                };

                return this.detail;
            });
        },


        searchSuperUserCandidates(term) {
            return SupportTeamApi.searchSuperUserCandidates(term);
        },

        searchWebsites(term) {
            return SupportTeamApi.searchWebsites(term);
        },
    },
});
